import { useRef, useState } from "react";
import { Button } from "@/components/Button";
import { useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { call, CALLABLES } from "@/services/callables";
import { MAX_UPLOAD_BYTES, type ParsedUpload } from "@/types";

/**
 * Step one: read the file.
 *
 * The 2 MB limit is stated before anything is chosen rather than after a
 * failed upload, because a school export that is too large is usually the wrong
 * file entirely — a whole-year extract rather than a class list — and knowing
 * the limit first is what makes that obvious.
 */
export function UploadStep({ onParsed }: { onParsed: (parsed: ParsedUpload) => void }) {
  const t = useT();
  const { showError } = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [parsed, setParsed] = useState<ParsedUpload | undefined>(undefined);
  const [file, setFile] = useState<File | undefined>(undefined);

  const read = async (chosen: File, sheetName?: string) => {
    if (chosen.size > MAX_UPLOAD_BYTES) {
      showError({ code: "invalid-argument", message: "import.error.fileTooLarge" });
      return;
    }

    setBusy(true);
    setFile(chosen);
    try {
      const buffer = await chosen.arrayBuffer();
      // Chunked, because `String.fromCharCode(...bytes)` on a 2 MB array
      // overflows the call stack — a failure that only appears on the large
      // files this limit already tolerates.
      const bytes = new Uint8Array(buffer);
      let binary = "";
      for (let offset = 0; offset < bytes.length; offset += 8192) {
        binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
      }

      const response = await call<
        { filename: string; content: string; sheetName?: string },
        ParsedUpload
      >(CALLABLES.parseImportFile, {
        filename: chosen.name,
        content: btoa(binary),
        ...(sheetName ? { sheetName } : {}),
      });

      // A spreadsheet with several sheets needs a choice before going on.
      if (response.sheetNames && response.sheetNames.length > 1 && !sheetName) {
        setParsed(response);
        return;
      }
      onParsed(response);
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const dropped = event.dataTransfer.files[0];
          if (dropped) void read(dropped);
        }}
        className={`rounded-card border-2 border-dashed px-6 py-12 text-center transition-colors ${
          dragging ? "border-brand bg-brand-soft" : "border-line bg-white"
        }`}
      >
        <p className="text-ink">{busy ? t("import.upload.reading") : t("import.upload.drop")}</p>
        <p className="mt-2 text-sm text-ink-muted">{t("import.upload.limit")}</p>

        <input
          ref={input}
          type="file"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="sr-only"
          onChange={(event) => {
            const chosen = event.target.files?.[0];
            if (chosen) void read(chosen);
          }}
        />
        {/* The one thing to do on this screen, so it is the one filled button. */}
        <Button busy={busy} className="mt-6" onClick={() => input.current?.click()}>
          {t("import.upload.choose")}
        </Button>
      </div>

      {parsed?.sheetNames && parsed.sheetNames.length > 1 ? (
        <div className="mt-6">
          <p className="field-label">{t("import.upload.sheet")}</p>
          <div className="flex flex-wrap gap-2">
            {parsed.sheetNames.map((sheet) => (
              <Button
                key={sheet}
                variant="secondary"
                busy={busy}
                onClick={() => file && void read(file, sheet)}
              >
                {sheet}
              </Button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
